import { createContainer } from 'shared-components/unstated'
import { CommentHubAPI, type CommentHubRole } from 'shared-code/commenthub-api'
import { useRef, type ReactNode } from 'react'

/** Where a role badge is drawn: the conversation panel draws everything one size down. */
export interface CommentHubRoleBadgeContext {
  layout?: 'panel'
}

interface CommentHubValueProps {
  apiUrl?: string
  /**
   * Apex hostname this brand is registered under in commentHub, e.g.
   * `soundguys.com`. Scopes the notification bell to one property.
   *
   * Deliberately explicit rather than derived from `location.hostname`: staging
   * runs on `staging.<brand>.com` and commentHub keys on the apex, so deriving
   * would send a hostname the server has never heard of and silently empty the
   * tray. Unset means "send no site filter" — the reader sees events from both
   * properties, which is a visible, recoverable state rather than a blank one.
   */
  site?: string
  /**
   * The app's own origin (`https://www.soundguys.com`). A notification posted by another service
   * may link only here (or to a root-relative path) — `notification-copy` `siteEventHref`.
   * Unset = only root-relative links.
   */
  appUrl?: string
  /**
   * The brand's badge for a comment author's commentHub role, drawn after the name in place of
   * the widget's own role pill (the raw role word: "Admin", "Moderator", "Staff", "Trusted").
   * Unset = the widget's pill (Android Authority). SoundGuys maps the team roles (moderator,
   * admin, staff) to its SOUNDGUYS TEAM pill and everything else to nothing (2026-10-23).
   * Presentation only: null draws no badge.
   */
  roleBadge?: (role: CommentHubRole, context?: CommentHubRoleBadgeContext) => ReactNode
  /**
   * The brand draws emoji reactions (the pill row and the picker, `comments/ReactionBar`) in place
   * of the thumbs up / down under a comment. Both brands set it (SoundGuys 2026-10-28, Android
   * Authority 2026-10-29); unset = the thumbs. A
   * frame's `voteStyle: 'helpful'` (SoundGuys discussion answers) wins over this either way: that
   * vote ranks answers and feeds points, and is not a reaction.
   */
  reactions?: boolean
}

interface CommentHubContextProps extends CommentHubValueProps {
  api: CommentHubAPI | null
}

// This context deliberately tolerates a missing apiUrl (unset env on a preview):
// consumers get a null api and are expected to render nothing.
export const CommentHubAPIContext = createContainer<CommentHubContextProps, CommentHubValueProps>(
  initial => {
    const ref = useRef<CommentHubAPI | null>(
      initial?.apiUrl ? new CommentHubAPI({ apiUrl: initial.apiUrl }) : null
    )
    return {
      api: ref.current,
      ...initial,
    }
  }
)
